import { Button, ButtonProps, Tooltip, Text, rem } from '@mantine/core';
import { forwardRef, MouseEvent, PropsWithChildren } from 'react';
import styled from 'styled-components';

interface Props extends ButtonProps {
	text?: string;
	tooltipLabel?: string;
	onClick?: (event: MouseEvent<HTMLElement>) => void;
	active?: boolean;
	ref?: React.Ref<HTMLButtonElement>;
}

const StyledTooltipButton = styled(Button)<ButtonProps & PropsWithChildren<Props>>`
	border: 1px solid transparent;
	border-left-color: ${({ theme }) => theme.v3.colors.gray[2]};
	width: 100%;
	min-width: fit-content;
	color: ${({ theme }) => theme.v3.colors.dark[0]};
	background-color: transparent;
	border-radius: 0;

	/* The v7 theme forces \`padding: 8px !important\` and \`height: auto !important\`
	   on all md buttons (for regular buttons like Get Help); that clobbers the
	   toolbar button's v6 12px inset and fixed height, letting buttons with a
	   leftSection icon (e.g. the Filters toggle) render taller than icon-less ones
	   (e.g. the Invalid quick filter). Out-specify and out-important both so every
	   toolbar button stays a consistent 12px / 3rem regardless of icon presence. */
	&& {
		padding: ${rem('12px')} !important;
		height: 3rem !important;
	}

	/* v7 has no \`unstyled\` variant, so it falls back to a filled-blue hover
	   (blue bg + white text). Keep the toolbar button flat on hover. */
	&&:hover {
		background-color: transparent;
		color: ${({ theme }) => theme.v3.colors.dark[0]};
	}

	&&.active,
	&&:active,
	&&.active:hover {
		background-color: ${({ theme }) => theme.v3.colors.blue[0]};
		border-color: ${({ theme }) => theme.v3.colors.blue[2]};
		color: ${({ theme }) => theme.v3.colors.dark[0]};
	}
`;

export const TooltipButton = forwardRef<HTMLButtonElement, Props>(
	({ variant, text, active, tooltipLabel, onClick, className, ...rest }, ref) => {
		const StyledButton = (
			<StyledTooltipButton
				ref={ref}
				onClick={onClick}
				variant={variant || 'unstyled'}
				{...rest}
				className={[active && 'active', className].filter(Boolean).join(' ')}
			>
				<Text size="14px" fw={400} lineClamp={1}>
					{text}
				</Text>
			</StyledTooltipButton>
		);
		return tooltipLabel ? <Tooltip label={tooltipLabel}>{StyledButton}</Tooltip> : StyledButton;
	}
);

TooltipButton.displayName = 'TooltipButton';
